'use client'

import Overlay from '@/app/components/Overlay'
import { Icons } from '@/app/components/ui/Icons'
import { emitSortingOptionClickGAEvent } from '@/app/ga-event-helpers'
import { smoothScrollTo } from '@/app/utils'
import { useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { FC, useRef, useState } from 'react'
import { RemoveScroll } from 'react-remove-scroll'
import { MD_BREAKPOINT, SM_HEIGHT_BREAKPOINT } from '../constants'
import { DestPagesProps, SORT_BY, SORT_DIR, SortOption } from '../types'
import Button from './common/Button'
import Popup from './common/Popup'
import Text from './common/Text'

export interface Props {
	pageProps: DestPagesProps
	defaultValueBy?: SORT_BY
	defaultValueDir?: SORT_DIR
	onOpen: () => void
	onClose: () => void
	onHashChange: (hash: string | null) => void
}

const HotelListSorting: FC<Props> = ({
	pageProps,
	defaultValueBy = SORT_BY.POPULARITY,
	defaultValueDir,
	onOpen: onPropsOpen,
	onClose: onPropsClose,
	onHashChange,
}) => {
	const t = useTranslations('Keywords')
	const tSorting = useTranslations('Sorting')

	const SORT_OPTIONS: SortOption[] = [
		{
			title: tSorting('bestMatch'),
			shortTitle: tSorting('bestMatch'),
			by: SORT_BY.POPULARITY,
		},
		{
			title: `${tSorting('price')} (${tSorting('lowToHigh')})`,
			shortTitle: tSorting('price'),
			by: SORT_BY.MIN_RATE,
			dir: SORT_DIR.ASC,
		},
		{
			title: `${tSorting('price')} (${tSorting('highToLow')})`,
			shortTitle: tSorting('price'),
			by: SORT_BY.MIN_RATE,
			dir: SORT_DIR.DESC,
		},
		{
			title: `${tSorting('starRating')} (${tSorting('highestFirst')})`,
			shortTitle: tSorting('starRating'),
			by: SORT_BY.STAR_RATING,
			dir: SORT_DIR.DESC,
		},
		{
			title: tSorting('distance'),
			shortTitle: tSorting('distance'),
			by: SORT_BY.DISTANCE,
			dir: SORT_DIR.ASC,
		},
	]

	const isTabletUp = useMediaQuery(MD_BREAKPOINT)
	const [isOpen, setIsOpen] = useState(false)
	const sortingRef = useRef<HTMLDivElement>(null)
	const smHeight = useMediaQuery(SM_HEIGHT_BREAKPOINT)

	const hasScrolled = useRef(false)

	const getValue = (by: SORT_BY, dir?: SORT_DIR) => {
		return SORT_OPTIONS.find(
			(option) => option.by === by && option.dir === dir
		)!
	}

	const [initialValue, _] = useState<SortOption>(
		getValue(
			pageProps.searchParams.by || defaultValueBy,
			pageProps.searchParams.dir || defaultValueDir
		)
	)

	const dropdownRef = useRef<HTMLDivElement>(null)

	// useEffect(() => {
	// 	if (sortingRef.current && dropdownRef.current) {
	// 		const sortingRefRect = sortingRef.current.getBoundingClientRect()
	// 		const scrollY = window.scrollY

	// 		if (smHeight && isTabletUp) {
	// 			eventBus.publish(PS_EVENTS.AUTO_SCROLL)
	// 			smoothScrollTo(sortingRefRect.y + scrollY, -20)
	// 			hasScrolled.current = true
	// 		}
	// 	}
	// 	if (!isOpen && hasScrolled.current) {
	// 		smoothScrollTo(0, 0)
	// 		hasScrolled.current = false
	// 	}
	// }, [isOpen])

	const handleOpen = () => {
		setIsOpen(!isOpen)
		onPropsOpen()
		// onHashChange(`#${FILTER.SORTING}`)
	}

	const handleClose = () => {
		setIsOpen(false)
		onPropsClose()
		// onHashChange(null)
	}

	const generateQuery = (value: SortOption) => {
		const { page, ...restParams } = pageProps.searchParams
		const query: DestPagesProps['searchParams'] = {
			...restParams,
			by: value.by,
			dir: value.dir,
		}
		return query
	}

	const handleSortSelect = (option: SortOption) => {
		emitSortingOptionClickGAEvent(option)
		if (!isTabletUp) {
			smoothScrollTo(0, 0)
		}
	}

	const getSortIcon = (sortOption: SortOption) => {
		const isPriceSort = sortOption.by === SORT_BY.MIN_RATE
		const isPriceAsc = isPriceSort && sortOption.dir === SORT_DIR.ASC
		const isPriceDesc = isPriceSort && sortOption.dir === SORT_DIR.DESC

		if (isPriceAsc) return <Icons.iconPriceUp className="me-2" />
		if (isPriceDesc) return <Icons.iconPriceDown className="me-2" />
		return <Icons.iconTopRating className="me-2" />
	}

	return (
		<div className="hotel-list-sorting select-none relative" ref={sortingRef}>
			<div className="flex items-center">
				<Button
					className={classNames(
						'flex items-center border-text-300 font-semibold rounded-xl text-sm h-[40px] px-3 py-2 hover:border-main',
						{
							'md:z-30 md:border-main': isOpen,
						}
					)}
					fontSize="base"
					border
					name="Sort By"
					onClick={handleOpen}
				>
					{(() => {
						const currentSort = getValue(
							pageProps.searchParams.by || defaultValueBy,
							pageProps.searchParams.dir || defaultValueDir
						)
						return (
							<>
								{getSortIcon(currentSort)}
								<span className="truncate whitespace-nowrap max-w-[120px] md:max-w-[200px]">
									{currentSort.shortTitle}
								</span>
							</>
						)
					})()}
					<Icons.caret
						className={classNames('ms-2 transition-all flex-shrink-0', {
							'rotate-180': isOpen,
						})}
					/>
				</Button>
			</div>
			{!isTabletUp && (
				<Overlay
					isOpened={isOpen}
					openInput={handleClose}
					disableScroll={true}
				/>
			)}
			<Popup
				className="hotel-list-sorting__popup"
				isOpen={isOpen}
				onClose={handleClose}
			>
				<RemoveScroll enabled={!isTabletUp && isOpen} removeScrollBar={false}>
					<div className="hotel-list-sorting__dropdown" ref={dropdownRef}>
						<div className="flex items-center justify-start p-4 md:hidden">
							<Icons.iconClose onClick={handleClose} className="me-4" />
							<Text font="bold">{t('sortBy')}</Text>
						</div>
						{SORT_OPTIONS.map((item) => {
							return (
								<Link
									href={{ query: generateQuery(item) }}
									key={item.title}
									onClick={() => handleSortSelect(item)}
									className={classNames(
										'flex items-center hotel-list-sorting__item',
										{
											'hotel-list-sorting__item_selected':
												item.by === initialValue.by &&
												item.dir === initialValue.dir,
										}
									)}
								>
									<div className="hotel-list-sorting__item-radio-container">
										<div className="hotel-list-sorting__item-radio"></div>
									</div>

									<Text size="sm">{item.title}</Text>
								</Link>
							)
						})}
					</div>
				</RemoveScroll>
			</Popup>
		</div>
	)
}

export default dynamic(() => Promise.resolve(HotelListSorting), {
	ssr: false,
})
